import { InboxOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ImportReport } from "@wms/domain";
import { Alert, Button, Checkbox, Modal, Space, Table, Typography, Upload } from "antd";
import { useState } from "react";
import { API_BASE, COMPANY_HEADER, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";

interface Props {
  entity: string;
  label: string;
  open: boolean;
  onClose: () => void;
}

export function ImportModal({ entity, label, open, onClose }: Props) {
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);
  // ТЗ «Понятные шаги» UX1-062 (section 24.2): the import has its own fetch — the working organization goes with it
  // like with every request of api/client.ts, so the server checks the same scope
  const company = useAuthStore((s) => s.company);
  const headers = (): Record<string, string> => ({ Authorization: `Bearer ${token}`, ...(company ? { [COMPANY_HEADER]: company.id } : {}) });
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [report, setReport] = useState<ImportReport | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append("file", file!);
      const response = await fetch(`${API_BASE}/import/${entity}?dry_run=${dryRun}`, {
        method: "POST",
        headers: headers(),
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail ?? data.title ?? "Ошибка импорта");
      return data as ImportReport;
    },
    onSuccess: (data) => {
      setReport(data);
      if (!data.dry_run) queryClient.invalidateQueries({ queryKey: [entity] });
    },
  });

  const downloadTemplate = async () => {
    const response = await fetch(`${API_BASE}/import/${entity}/template.xlsx`, { headers: headers() });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entity}-template.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const close = () => {
    setFile(null);
    setReport(null);
    onClose();
  };

  return (
    <Modal title={`Импорт: ${label}`} open={open} onCancel={close} footer={null} width={720} destroyOnHidden>
      <Typography.Paragraph type="secondary">
        Файл XLSX или CSV с заголовками из шаблона. Ссылочные поля принимают код связанной записи или «код — название»
        из выгрузки списка, поэтому выгруженный файл можно поправить и загрузить обратно. Существующие записи находятся по
        коду и обновляются.
      </Typography.Paragraph>
      <Upload.Dragger
        accept=".xlsx,.csv"
        maxCount={1}
        beforeUpload={(f) => {
          setFile(f);
          setReport(null);
          setDryRun(true); // a new file is always checked first, whatever the previous import did
          return false;
        }}
        onRemove={() => setFile(null)}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Перетащите файл или нажмите для выбора</p>
      </Upload.Dragger>
      <Space style={{ marginTop: 16, width: "100%", justifyContent: "space-between" }} wrap>
        <Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}>
          Только проверить, ничего не записывать
        </Checkbox>
        <Space>
          <Button onClick={downloadTemplate}>Скачать шаблон</Button>
          <Button type="primary" disabled={!file} loading={run.isPending} onClick={() => run.mutate()}>
            {dryRun ? "Проверить" : "Импортировать"}
          </Button>
        </Space>
      </Space>
      {run.error && <Alert type="error" style={{ marginTop: 16 }} message={errorMessage(run.error)} showIcon />}
      {report && (
        <div style={{ marginTop: 16 }}>
          <Alert
            type={report.failed ? "warning" : "success"}
            showIcon
            message={
              report.dry_run
                ? `Проверка: строк ${report.total}. Будет создано ${report.created}, будет обновлено ${report.updated}, с ошибками ${report.failed}.`
                : `Импорт: строк ${report.total}. Создано ${report.created}, обновлено ${report.updated}, с ошибками ${report.failed}.`
            }
            description={
              report.dry_run
                ? "Это только проверка: в справочник ничего не записано. Снимите флажок «Только проверить», чтобы загрузить данные."
                : report.total === 0
                  ? "В файле нет строк с данными: заполните строки под заголовками шаблона."
                  : undefined
            }
          />
          {report.errors.length > 0 && (
            <Table
              size="small"
              style={{ marginTop: 12 }}
              rowKey="row"
              pagination={{ pageSize: 10 }}
              dataSource={report.errors}
              columns={[
                { title: "Строка", dataIndex: "row", width: 90 },
                { title: "Ошибка", dataIndex: "message" },
              ]}
            />
          )}
          {/* KZ 30.10.2026 (ТЗ реквизитов §10.2): accepted, but not taken from the file — the ИНН / КПП of an old template */}
          {(report.warnings?.length ?? 0) > 0 && (
            <Table
              size="small"
              style={{ marginTop: 12 }}
              rowKey={(w) => `${w.row}-${w.message}`}
              pagination={{ pageSize: 10 }}
              dataSource={report.warnings}
              data-testid="import-warnings"
              columns={[
                { title: "Строка", dataIndex: "row", width: 90, render: (row: number) => (row === 1 ? "файл" : row) },
                { title: "Замечание", dataIndex: "message" },
              ]}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
